import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollText, CheckCircle2, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function ApologiesAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [decideFor, setDecideFor] = useState(null);
  const [decideStatus, setDecideStatus] = useState("approved");
  const [decideNote, setDecideNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const params = filter !== "all" ? `?status=${filter}` : "";
    const { data } = await api.get(`/apologies${params}`);
    setItems(data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const submitDecision = async () => {
    if (decideStatus === "more_info" && decideNote.trim().length < 5) {
      toast.error("Please add a note explaining what's missing");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/apologies/${decideFor.id}/decide`, {
        status: decideStatus,
        decision_note: decideNote.trim() || null,
      });
      toast.success(decideStatus === "approved" ? "Approved — employee reactivated" : "Sent back for more info");
      setDecideFor(null);
      setDecideNote("");
      setDecideStatus("approved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Conduct</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Apology Letters</h1>
        <p className="text-sm text-zinc-500 mt-2">Review apology letters from deactivated employees. Approving reactivates them.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "more_info", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded ${filter === s ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            data-testid={`apologies-filter-${s}`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-md p-8 text-center text-zinc-400">
          <ScrollText className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
          No apologies in this view.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="bg-white border border-zinc-200 rounded-md p-5" data-testid={`apology-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-semibold">{a.user_name}</div>
                  <div className="text-[11px] text-zinc-500 uppercase tracking-widest">{(a.user_role || "").replace("_", " ")} · {fmtDate(a.created_at)}</div>
                </div>
                <StatusPill status={a.status} />
              </div>
              <div className="mt-3 text-sm whitespace-pre-wrap text-zinc-800">{a.content}</div>
              {a.decision_note && (
                <div className="mt-3 text-[12px] italic text-zinc-600 border-t border-zinc-200 pt-2">
                  Decision note: {a.decision_note}
                </div>
              )}
              {a.status === "pending" && (
                <div className="mt-4 flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => { setDecideFor(a); setDecideStatus("more_info"); setDecideNote(""); }}
                    data-testid={`apology-moreinfo-${a.id}`}
                  >
                    <MessageSquareWarning className="w-4 h-4 mr-1" /> Ask for more
                  </Button>
                  <Button
                    onClick={() => { setDecideFor(a); setDecideStatus("approved"); setDecideNote(""); }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`apology-approve-${a.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve &amp; reactivate
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!decideFor} onOpenChange={(o) => !o && setDecideFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {decideStatus === "approved" ? "Approve apology" : "Ask for more information"}
            </DialogTitle>
            <DialogDescription>
              {decideStatus === "approved"
                ? `${decideFor?.user_name}'s account will be re-activated. Warnings stay on record.`
                : `Tell ${decideFor?.user_name} what additional detail you need.`}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Textarea
              rows={4}
              value={decideNote}
              onChange={(e) => setDecideNote(e.target.value)}
              placeholder={decideStatus === "approved" ? "Optional note" : "What specifically should they add or clarify?"}
              data-testid="apology-decision-note"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={submitDecision}
              disabled={busy}
              className={decideStatus === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-zinc-950 hover:bg-zinc-800"}
              data-testid="apology-decision-submit"
            >
              {decideStatus === "approved" ? "Approve" : "Send back"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: ["bg-amber-50", "text-amber-700"],
    approved: ["bg-emerald-50", "text-emerald-700"],
    more_info: ["bg-orange-50", "text-orange-700"],
  };
  const [bg, text] = map[status] || ["bg-zinc-100", "text-zinc-600"];
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${bg} ${text}`}>
      {status.replace("_", " ")}
    </span>
  );
}
